import React, { useEffect, useMemo, useState } from "react";

import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";

import styled from "styled-components";

import api from "../../../services/api";
import {
  detalheDaDescricao,
  hhmmDeMinutos,
} from "../../../utils/horaDoProcesso";

interface HoraDoProcesso {
  id: number;
  data: string;
  minutos: number;
  descricao: string;
  referencia: string | null;
  responsavel: string;
  tipoResponsavel: "adv" | "adm";
  faturada: boolean;
  documento: string | null;
}

interface ResumoDasHoras {
  totalMinutos: number;
  mesAtualMinutos: number;
  quantidade: number;
  ultima: { data: string; responsavel: string } | null;
  porMes: { mes: string; minutos: number }[];
  porPessoa: {
    responsavel: string;
    tipoResponsavel: "adv" | "adm";
    minutos: number;
    ultimaData: string;
  }[];
  horas: HoraDoProcesso[];
}

interface Props {
  processoId: number;
  versao: number;
}

const MESES = [
  "jan",
  "fev",
  "mar",
  "abr",
  "mai",
  "jun",
  "jul",
  "ago",
  "set",
  "out",
  "nov",
  "dez",
];

const rotuloDoMes = (mes: string) => {
  const [ano, m] = mes.split("-");
  return `${MESES[Number(m) - 1]}/${ano}`;
};

const dataBR = (iso: string) => iso.split("-").reverse().join("/");

const Painel = styled.div`
  font-size: 13px;

  .kpis {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 20px;
  }

  .kpi {
    padding: 12px 14px;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    background: #fff;

    span {
      display: block;
      font-size: 11px;
      color: #6c757d;
      text-transform: uppercase;
    }

    strong {
      display: block;
      font-size: 20px;
      font-weight: 600;
      margin: 4px 0 2px;
    }

    small {
      color: #6c757d;
    }
  }

  .kpi-texto strong {
    font-size: 15px;
    margin-top: 8px;
  }

  .tabelas {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
    gap: 20px;
    margin-bottom: 20px;
  }

  h4 {
    margin: 0 0 8px;
    font-size: 14px;
    font-weight: 600;
  }

  .cabecalho-lista {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;

    h4 {
      margin: 0;
    }
  }

  .p-datatable .p-datatable-tbody > tr > td,
  .p-datatable .p-datatable-thead > tr > th {
    padding: 6px 10px;
    font-size: 13px;
  }

  .linha-selecionada {
    background: #e3f2fd !important;
    font-weight: 600;
  }

  .descricao {
    white-space: pre-line;
    max-height: 4.5em;
    overflow: hidden;
  }

  .descricao-aberta {
    white-space: pre-line;
  }

  .numero {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  @media (max-width: 960px) {
    .kpis {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .tabelas {
      grid-template-columns: 1fr;
    }
  }
`;

const HorasDoProcesso: React.FC<Props> = ({ processoId, versao }) => {
  const [resumo, setResumo] = useState<ResumoDasHoras | null>(null);
  const [erro, setErro] = useState("");
  const [carregando, setCarregando] = useState(true);
  const [mesSelecionado, setMesSelecionado] = useState<string | null>(null);
  const [abertas, setAbertas] = useState<number[]>([]);

  useEffect(() => {
    setCarregando(true);
    setErro("");
    api
      .get<ResumoDasHoras>(`/management/workinghours/processo/${processoId}`)
      .then(({ data }) => {
        setResumo(data);
        setMesSelecionado((atual) => atual ?? data.porMes[0]?.mes ?? null);
      })
      .catch((error) => {
        setErro(
          error?.response?.status === 403
            ? "O resumo de horas é restrito a administradores."
            : "Não foi possível carregar as horas deste processo."
        );
      })
      .finally(() => setCarregando(false));
  }, [processoId, versao]);

  const horasDaLista = useMemo(() => {
    if (!resumo) return [];
    if (!mesSelecionado) return resumo.horas;
    return resumo.horas.filter((h) => h.data.startsWith(mesSelecionado));
  }, [resumo, mesSelecionado]);

  if (carregando && !resumo) {
    return (
      <div className="flex justify-content-center p-4">
        <ProgressSpinner style={{ width: "40px", height: "40px" }} />
      </div>
    );
  }

  if (erro) return <Message severity="warn" text={erro} />;

  if (!resumo || resumo.quantidade === 0) {
    return (
      <Message
        severity="info"
        text='Nenhuma hora encontrada para este processo. As horas antigas são encontradas pelo número que aparece em "Autos:" na descrição.'
      />
    );
  }

  const alternarDescricao = (id: number) =>
    setAbertas((atual) =>
      atual.includes(id) ? atual.filter((a) => a !== id) : [...atual, id]
    );

  return (
    <Painel>
      <div className="kpis">
        <div className="kpi">
          <span>Total no processo</span>
          <strong>{hhmmDeMinutos(resumo.totalMinutos)}</strong>
          <small>{resumo.quantidade} lançamentos</small>
        </div>
        <div className="kpi">
          <span>Este mês</span>
          <strong>{hhmmDeMinutos(resumo.mesAtualMinutos)}</strong>
          <small>
            {resumo.mesAtualMinutos > 0 ? "horas lançadas" : "nada lançado"}
          </small>
        </div>
        <div className="kpi">
          <span>Pessoas envolvidas</span>
          <strong>{resumo.porPessoa.length}</strong>
          <small>
            {resumo.porPessoa.filter((p) => p.tipoResponsavel === "adv").length}{" "}
            advogado(s) ·{" "}
            {resumo.porPessoa.filter((p) => p.tipoResponsavel === "adm").length}{" "}
            assistente(s)
          </small>
        </div>
        <div className="kpi kpi-texto">
          <span>Última hora lançada</span>
          <strong>{resumo.ultima ? dataBR(resumo.ultima.data) : "—"}</strong>
          <small>{resumo.ultima?.responsavel}</small>
        </div>
      </div>

      <div className="tabelas">
        <div>
          <h4>Mês a mês</h4>
          <DataTable
            value={resumo.porMes}
            dataKey="mes"
            scrollable
            scrollHeight="260px"
            rowClassName={(m) => ({
              "linha-selecionada": m.mes === mesSelecionado,
            })}
            onRowClick={(e) => setMesSelecionado(e.data.mes)}
            className="p-datatable-sm cursor-pointer"
          >
            <Column header="Mês" body={(m) => rotuloDoMes(m.mes)} />
            <Column
              header="Horas"
              body={(m) => hhmmDeMinutos(m.minutos)}
              bodyClassName="numero"
              headerClassName="numero"
            />
          </DataTable>
        </div>
        <div>
          <h4>Por pessoa</h4>
          <DataTable
            value={resumo.porPessoa}
            dataKey="responsavel"
            scrollable
            scrollHeight="260px"
            className="p-datatable-sm"
          >
            <Column
              header="Pessoa"
              body={(p) => (
                <>
                  {p.responsavel}{" "}
                  {p.tipoResponsavel === "adm" ? (
                    <Tag value="assistente" severity="info" />
                  ) : null}
                </>
              )}
            />
            <Column
              header="Horas"
              body={(p) => hhmmDeMinutos(p.minutos)}
              bodyClassName="numero"
              headerClassName="numero"
            />
            <Column header="Última" body={(p) => dataBR(p.ultimaData)} />
          </DataTable>
        </div>
      </div>

      <div className="cabecalho-lista">
        <h4>
          {mesSelecionado
            ? `Horas de ${rotuloDoMes(mesSelecionado)}`
            : "Todas as horas"}{" "}
          <small>
            ({horasDaLista.length} ·{" "}
            {hhmmDeMinutos(horasDaLista.reduce((t, h) => t + h.minutos, 0))})
          </small>
        </h4>
        <Button
          type="button"
          label={mesSelecionado ? "Ver todas as horas" : "Ver só um mês"}
          icon={mesSelecionado ? "pi pi-list" : "pi pi-calendar"}
          className="p-button-sm p-button-outlined"
          onClick={() =>
            setMesSelecionado(mesSelecionado ? null : resumo.porMes[0]?.mes)
          }
        />
      </div>
      <DataTable
        value={horasDaLista}
        dataKey="id"
        paginator={horasDaLista.length > 15}
        rows={15}
        className="p-datatable-sm"
      >
        <Column
          header="Data"
          body={(h) => dataBR(h.data)}
          style={{ width: "90px" }}
        />
        <Column
          header="Responsável"
          field="responsavel"
          style={{ width: "190px" }}
        />
        <Column
          header="Duração"
          body={(h) => hhmmDeMinutos(h.minutos)}
          bodyClassName="numero"
          headerClassName="numero"
          style={{ width: "80px" }}
        />
        <Column
          header="Descrição"
          body={(h) => (
            <div
              className={
                abertas.includes(h.id) ? "descricao-aberta" : "descricao"
              }
              onClick={() => alternarDescricao(h.id)}
              role="none"
              title="Clique para ver a descrição completa"
            >
              {abertas.includes(h.id)
                ? h.descricao
                : detalheDaDescricao(h.descricao)}
            </div>
          )}
        />
        <Column
          header="Fatura"
          body={(h) =>
            h.faturada ? <Tag value={h.documento} severity="success" /> : "—"
          }
          style={{ width: "110px" }}
        />
      </DataTable>
    </Painel>
  );
};

export default HorasDoProcesso;
